import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useNewJourneyStore } from '../store/new-journey';
import { FreeGatingEnum, useFreeGatingStore } from 'src/common/free-gating/store';
import { useFeatureRestrictions } from 'src/hooks/useFeatureRestrictions';
import {
  AddRegularIcon,
  AutomagicRegularIcon,
  DuplicateRegularIcon,
  FolderRegularIcon,
  MagicRegularIcon,
  ConvertRegularIcon,
  CloseRegularIcon,
} from 'src/monet/icons';
import { CreateJourneyMethodType } from './widget';
import { easings, animated, useSpringRef, useSpring } from '@react-spring/web';

interface CreateJourneyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onMethodSelect: (type: CreateJourneyMethodType) => void;
}

const ANIMATION_CONFIG = {
  mass: 1,
  tension: 400,
  friction: 40,
};

export const CreateJourneyFullModal = ({ isOpen, onClose, onMethodSelect }: CreateJourneyModalProps) => {
  const history = useHistory();
  const createJourneyWithHistory = useNewJourneyStore((state) => () => state.createJourney(history));
  const setFreeGatingModalByType = useFreeGatingStore((state) => state.setFreeGatingModalByType);
  const { features } = useFeatureRestrictions();

  const animationApi = useSpringRef();
  const [animationStyle] = useSpring(
    () => ({
      ref: animationApi,
      from: { opacity: 0, scale: 0.9 },
      to: { opacity: 1, scale: 1 },
      config: ANIMATION_CONFIG,
    }),
    []
  );

  useEffect(() => {
    if (isOpen) {
      animationApi.start({
        from: { opacity: 0, scale: 0.9 },
        to: { opacity: 1, scale: 1 },
        config: ANIMATION_CONFIG,
      });
    } else {
      animationApi.start({
        to: { opacity: 0, scale: 0.9 },
        config: { duration: 200, easing: easings.easeOutCubic },
      });
    }
  }, [isOpen, animationApi]);

  const handlePresentationMode = () => {
    // if (isFreeOrg || (!isFreeOrg && isStarter)) {
    //   setFreeGatingModalByType(FreeGatingEnum.PRESENTATION_MODE, true);
    //   onClose();
    //   return;
    // }
    onClose();
    createJourneyWithHistory();
  };

  const handleLandingPageMode = () => {
    onClose();
    onMethodSelect('blank');
  };

  const handleDuplicateJourney = () => {
    onClose();
    onMethodSelect('duplicate');
  };

  const handleOrganizationTemplate = () => {
    if (!features.canAccessOrganizationTemplates) {
      setFreeGatingModalByType(FreeGatingEnum.ORG_TEMPLATES, true);
      onClose();
      return;
    }
    onClose();
    onMethodSelect('organizationTemplate');
  };

  const handleCreateWithAI = () => {
    onClose();
    onMethodSelect('assistant');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
      <animated.div
        style={animationStyle}
        className="bg-white rounded-xl p-8 w-[560px] shadow-2xl text-gray-800 relative"
      >
        <button 
          className="absolute right-6 top-6 text-gray-500 hover:text-gray-800 transition-colors"
          onClick={onClose}
        >
          <CloseRegularIcon className="w-6 h-6" />
        </button>
        
        <h2 className="text-2xl font-semibold mb-8 text-gray-800">Create a Journey</h2>
        
        <div className="grid grid-cols-5 gap-5">
          {/* Pitch Mode */}
          <div
            className="group flex flex-col items-center px-3 py-5 rounded-xl border border-transparent hover:border-blue-200 hover:bg-blue-50 cursor-pointer transition-all duration-200 transform hover:-translate-y-1"
            onClick={handlePresentationMode}
          >
            <div className="bg-blue-100 rounded-full p-3 mb-4 group-hover:bg-blue-200 transition-colors">
              <MagicRegularIcon className="w-8 h-8 text-blue-700" />
            </div>
            <span className="text-sm font-medium text-center text-gray-800 mb-2">Pitch Mode</span>
            <button className="mt-auto px-4 py-1.5 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 hover:border-gray-400 shadow-sm transition-all">Start</button>
          </div>
          
          {/* Microsite Mode */}
          <div
            className="group flex flex-col items-center px-3 py-5 rounded-xl border border-transparent hover:border-purple-200 hover:bg-purple-50 cursor-pointer transition-all duration-200 transform hover:-translate-y-1"
            onClick={handleLandingPageMode}
          >
            <div className="bg-purple-100 rounded-full p-3 mb-4 group-hover:bg-purple-200 transition-colors">
              <ConvertRegularIcon className="w-8 h-8 text-purple-700" />
            </div>
            <span className="text-sm font-medium text-center text-gray-800 mb-2">Microsite Mode</span>
            <button className="mt-auto px-4 py-1.5 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 hover:border-gray-400 shadow-sm transition-all">Start</button>
          </div>
          
          {/* Duplicate Journey */}
          <div
            className="group flex flex-col items-center px-3 py-5 rounded-xl border border-transparent hover:border-teal-200 hover:bg-teal-50 cursor-pointer transition-all duration-200 transform hover:-translate-y-1"
            onClick={handleDuplicateJourney}
          >
            <div className="bg-teal-100 rounded-full p-3 mb-4 group-hover:bg-teal-200 transition-colors">
              <DuplicateRegularIcon className="w-8 h-8 text-teal-700" />
            </div>
            <span className="text-sm font-medium text-center text-gray-800 mb-2">Duplicate Journey</span>
            <button className="mt-auto px-4 py-1.5 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 hover:border-gray-400 shadow-sm transition-all">Start</button>
          </div>
          
          {/* Organization Template */}
          <div
            className="group flex flex-col items-center px-3 py-5 rounded-xl border border-transparent hover:border-amber-200 hover:bg-amber-50 cursor-pointer transition-all duration-200 transform hover:-translate-y-1"
            onClick={handleOrganizationTemplate}
          >
            <div className="bg-amber-100 rounded-full p-3 mb-4 group-hover:bg-amber-200 transition-colors">
              <FolderRegularIcon className="w-8 h-8 text-amber-700" />
            </div>
            <span className="text-sm font-medium text-center text-gray-800 mb-2">Organization Template</span>
            <button className="mt-auto px-4 py-1.5 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 hover:border-gray-400 shadow-sm transition-all">Start</button>
          </div>
          
          {/* Create with AI */}
          <div
            className="group flex flex-col items-center px-3 py-5 rounded-xl border border-transparent hover:border-pink-200 hover:bg-pink-50 cursor-pointer transition-all duration-200 transform hover:-translate-y-1"
            onClick={handleCreateWithAI}
          >
            <div className="bg-pink-100 rounded-full p-3 mb-4 group-hover:bg-pink-200 transition-colors">
              <AutomagicRegularIcon className="w-8 h-8 text-pink-700" />
            </div>
            <span className="text-sm font-medium text-center text-gray-800 mb-2">Create with AI</span>
            <button className="mt-auto px-4 py-1.5 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 hover:border-gray-400 shadow-sm transition-all">Start</button>
          </div>
        </div>
      </animated.div>
    </div>
  );
}; 